import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import images from "../../../assets/images";
import { ModalOnboarding } from "../modal-onboarding/modal-onboarding";
import { MessageFooter } from "../../chat/message-footer/message-footer";
import { ChatHeader } from "../../chat/chat-header";
import {
  OnboardingArrowIcon,
  OnboardingMenuButton,
  RoleplaysIcon,
} from "../../../assets/icon";
import { MessageBubble } from "../../chat/chat-bubble/message-bubble";
import { mockMessages } from "../moked-messages/mock-messages";
import { WhatsAppAudioRecorder } from "../../../components/chat/whatsapp-audio-recorder/whatsapp-audio-recorder";
import { MenuDropdown } from "../../../components/chat/menu-dropdown/menu-dropdown";
import { getMainMenu } from "../../../axios/buttons";
import { MenuButtonsProps } from "@/types";

interface InteractionOnboardingProps {
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleSkip: () => void;
}

export const InteractionOnboarding = ({
  handlePrevPage,
  handleNextPage,
  handleSkip,
}: InteractionOnboardingProps) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isLargeScreen = useMediaQuery("(min-width:1200px)");
  const isMobileLandscape = useMediaQuery(
    "(max-width: 1024px) and (orientation: landscape)",
  );
  const menuAnchor: HTMLElement | null = null;
  const [menuButtons, setMenuButtons] = useState<MenuButtonsProps[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isNextSlide, setIsNextSlide] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsRecording(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleNextSlide = () => {
    setIsNextSlide(true);
  };

  useEffect(() => {
    getMainMenu()
      .then((data: MenuButtonsProps[]) => {
        const roleplayMenuItem: MenuButtonsProps = {
          text: "Roleplays",
          description: "Start a Roleplay",
          command: "/open_roleplay",
          image: "",
          Icon: RoleplaysIcon,
        };
        const withoutRoleplay = data.filter((b) => b.text !== "Roleplays");
        setMenuButtons([roleplayMenuItem, ...withoutRoleplay]);
      })
      .catch((error) => console.error("Failed to load menu:", error));
  }, [isRecording]);

  return (
    <Box
      sx={{
        overflowX: "hidden",
        overflowY: isMobileLandscape ? "auto" : "hidden",
        position: "relative",
        minHeight: "100dvh",
        background: "linear-gradient(0deg, #50557033 0%, #505570 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: isMobileLandscape ? "flex-start" : "space-between",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: -2,
          pointerEvents: "none",
          filter: "blur(5px)",
        }}
      >
        <ChatHeader onLogout={() => {}} onMessageSelect={() => {}} />
        <Box
          sx={{
            overflowY: "scroll",
            maxWidth: "1000px",
            width: "100%",
            margin: "0 auto",
            px: "10px",
            "&::-webkit-scrollbar": { display: "none" },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
          }}
        >
          {mockMessages.map((msg, index) => (
            <MessageBubble
              key={msg.message_id}
              message={msg}
              isBot={msg.is_bot}
              bubbleIndex={index}
              headerVisible={false}
              prevMessageHeight={0}
              scrollToPreviousUserMessage={() => {}}
              scrollToNextUserMessage={() => {}}
              scrollToPreviousLessonMessage={() => {}}
              scrollToNextLessonMessage={() => {}}
              isStatic={true}
            />
          ))}
        </Box>
        <MessageFooter
          onSendMessage={() => {}}
          onRecordAudio={() => {}}
          onFileChange={() => {}}
          isUploading={false}
          isUploadingFile={false}
        />
      </Box>
      <ModalOnboarding
        isNextSlide={isNextSlide}
        handleSkip={handleSkip}
        buttonText={"Next"}
        handleNextPage={handleNextPage}
        handlePrevPage={handlePrevPage}
        handleNextSlide={handleNextSlide}
      />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <AnimatePresence>
          {!isNextSlide && (
            <motion.div
              key="bottomSection"
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    marginBottom: "30px",
                    marginLeft: "50px",
                  }}
                >
                  <motion.div
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, y: 190, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Box
                      component="img"
                      src={images.OnboardingBotSmall}
                      sx={{
                        position: "absolute",
                        left: { xs: -200, sm: -310, md: -300 },
                        bottom: { xs: -15, sm: -15, md: 0 },
                        width: "230px",
                        height: "auto",
                      }}
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    {!isRecording ? (
                      <motion.div
                        style={{ zIndex: 50 }}
                        key="message1"
                        initial={{ opacity: 0.5 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Box
                          component="img"
                          src={images.OnboardingBotMessage}
                          sx={{
                            position: "absolute",
                            left: { xs: -65, sm: -170, md: -170 },
                            top: { xs: -40, sm: -40, md: -56 },
                          }}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        style={{ zIndex: 50 }}
                        key="message2"
                        initial={{ opacity: 0.5 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Box
                          component="img"
                          src={images.OnboardingBotMessage2}
                          sx={{
                            position: "absolute",
                            left: { xs: -65, sm: -170, md: -170 },
                            top: { xs: -40, sm: -40, md: -56 },
                          }}
                        />
                      </motion.div>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.5 }}
                  >
                    <OnboardingArrowIcon />
                  </motion.div>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <Box sx={{ width: "100%", pointerEvents: "none" }}>
          <AnimatePresence mode="wait" initial={false}>
            {isNextSlide ? (
              <motion.div
                key="messageFooterBlurred"
                style={{
                  position: "relative",
                  maxWidth: "715px",
                  margin: "0 auto",
                }}
              >
                <Box
                  sx={{
                    zIndex: 10,
                    position: isMobileLandscape ? "relative" : "absolute",
                    bottom: isMobileLandscape ? "auto" : isMobile ? 68 : 50,
                      left: isMobileLandscape
                        ? 0
                        : isMobile
                          ? 0
                          : isLargeScreen
                            ? -200
                            : 8,
                    marginTop: isMobileLandscape ? 8 : 0,
                  }}
                >
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    style={{
                      transform: isMobileLandscape
                        ? "none"
                        : isMobile
                          ? "scale(0.78)"
                          : "none",
                      transformOrigin: "left bottom",
                    }}
                  >
                    <MenuDropdown
                      isStatic
                      anchorEl={menuAnchor}
                      open={true}
                      menuButtons={menuButtons}
                      onSendMessage={() => {}}
                      onClose={() => {}}
                    />
                  </motion.div>
                  <OnboardingMenuButton />
                </Box>
                <motion.div
                  initial={{ filter: "blur(3px)", opacity: 0 }}
                  animate={{ filter: "blur(3px)", opacity: 1 }}
                  exit={{ filter: "blur(3px)", opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <MessageFooter
                    onSendMessage={() => {}}
                    onRecordAudio={() => {}}
                    onFileChange={() => {}}
                    isUploading={false}
                    isUploadingFile={false}
                  />
                </motion.div>
              </motion.div>
            ) : !isRecording ? (
              <motion.div
                key="messageFooter"
                style={{ width: "100%" }}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 100, opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <MessageFooter
                  onSendMessage={() => {}}
                  onRecordAudio={() => {}}
                  onFileChange={() => {}}
                  isUploading={false}
                  isUploadingFile={false}
                />
              </motion.div>
            ) : (
              <motion.div
                key="voiceRecorder"
                style={{ width: "100%" }}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 0, opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <WhatsAppAudioRecorder
                  onCancel={() => {}}
                  isVisible={true}
                  isStatic={true}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};
